import { useEffect, useState } from "react";
import { config } from "../../utils/config";
import axios from "axios";
import { getCookie } from "../../utils/cookieHandler";
import DisplayDataItem from "./DisplayDataItem";
import { PropagateLoader } from "react-spinners";
import { findDataInFields } from "../../utils/helperFuctions";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/20/solid";

const ReservationSummary = ({
  id,
  currentTab,
  fields,
  isCollapsed,
  toggleCollapsed,
  isSaving,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState(null);
  const [prevIsSaving, setPrevIsSaving] = useState(false);

  const getReservationData = async () => {
    // Don't fetch if there's no valid ID (new reservation)
    if (!id || id === "new" || id === "undefined") {
      setData(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const resp = await axios.get(
        config.apiUrl + "/api/reservations" + "/" + id,
        { headers: { Authorization: `Bearer ${getCookie("_AT")}` } }
      );
      setData(resp.data);
      setIsLoading(false);
    } catch (error) {
      console.error("Error fetching reservation data:", error);
      setData(null);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getReservationData();
  }, [currentTab.name, id]);

  // Refresh data when save operation completes
  useEffect(() => {
    if (prevIsSaving && !isSaving) {
      // Save operation just completed, refresh data
      getReservationData();
    }
    setPrevIsSaving(isSaving);
  }, [isSaving]);

  const pickupOrReturn =
    currentTab.name === "Pickup" || currentTab.name === "Return";

  // Extract field values that are likely to change
  const balance = findDataInFields(fields, "balance");
  const security_deposit = findDataInFields(fields, "security_deposit");
  const branchCurrency = findDataInFields(fields, "currency.name");
  const pickupDate = findDataInFields(fields, "pickup_date");
  const returnDate = findDataInFields(fields, "return_date");
  const pickupNote = findDataInFields(fields, "pickup_note");
  const returnNote = findDataInFields(fields, "return_note");

  // Get time adjustment data directly from fields
  const timeExtensionField = fields.find((f) => f.name === "time_extension");
  const timeAdjustmentData = timeExtensionField?.defaultValue;
  // Filter out deleted items (items marked with delete: true)
  const activeTimeAdjustments =
    timeAdjustmentData && Array.isArray(timeAdjustmentData)
      ? timeAdjustmentData.filter((item) => !item.delete)
      : [];
  const latestTimeAdjustment =
    activeTimeAdjustments.length > 0
      ? activeTimeAdjustments[activeTimeAdjustments.length - 1].date_extended
      : null;

  return (
    <div className={`${isCollapsed ? "mb-8" : ""}`}>
      <div className="border-b border-gray-200 bg-gray-100 px-4 py-5 sm:px-6 rounded-t-lg">
        <div className="-ml-4 -mt-2 flex flex-wrap items-center justify-between sm:flex-nowrap">
          <div className="ml-4 mt-2 w-full flex justify-between">
            <h3 className="text-base font-semibold flex items-center text-gray-700">
              Reservation summary
            </h3>
          </div>
          <div className="ml-2 mt-2 flex-shrink-0">
            <span
              onClick={toggleCollapsed}
              className="inline-flex items-center cursor-pointer select-none transition-transform duration-200 hover:scale-110"
              style={{ padding: 4 }}
              title={isCollapsed ? "Expand summary" : "Collapse summary"}
            >
              {isCollapsed ? (
                <ChevronDownIcon className="w-8 h-8 text-purple-700 bg-purple-100 rounded-full p-1" />
              ) : (
                <ChevronUpIcon className="w-8 h-8 text-purple-700 bg-purple-100 rounded-full p-1" />
              )}
            </span>
          </div>
        </div>
      </div>

      {!isCollapsed && (
        <>
          {isLoading === true ? (
            <div className="flex justify-center pb-6">
              <PropagateLoader className="mt-1" color="#5729BC" />
            </div>
          ) : data == null ? (
            <div className="p-6 text-center text-gray-500">
              <p className="text-sm">Save the reservation to see the summary</p>
            </div>
          ) : (
            <div
              className="mb-8 shadow-sm transition-all duration-300 ease-in-out"
              style={{
                borderRight: "1px solid rgb(229, 231, 235)",
                borderLeft: "1px solid rgb(229, 231, 235)",
                borderBottom: "1px solid rgb(229, 231, 235)",
                borderBottomLeftRadius: 8,
                borderBottomRightRadius: 8,
              }}
            >
              {/* Main Grid Container */}
              <div className="grid grid-cols-4 max-xl:grid-cols-2 max-lg:grid-cols-1 gap-6 p-4">
                {/* Reservation Details Section */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
                      Reservation Details
                    </h3>
                    <div className="grid grid-cols-1 gap-2">
                      {/* Reservation Code and Status Display */}
                      {data && (
                        <div className="mb-2">
                          <div className="text-sm font-medium leading-6 text-gray-900 text-left">
                            Reservation code
                          </div>
                          <div className="flex items-center gap-2 sm:text-sm sm:leading-6 text-[#6b7280] font-medium">
                            {data.reservation_code && (
                              <span>{data.reservation_code}</span>
                            )}
                            {data.status && data.reservation_code && (
                              <span className="text-gray-400">|</span>
                            )}
                            {data.status && (
                              <span
                                className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${
                                  {
                                    "Picked up":
                                      "text-blue-700 ring-blue-600/20 bg-blue-50",
                                    Draft:
                                      "text-gray-600 bg-gray-50 ring-gray-500/10",
                                    Returned:
                                      "text-purple-700 ring-purple-600/40 bg-purple-50",
                                    Closed:
                                      "text-green-700 ring-green-600/20 bg-green-50",
                                    Canceled:
                                      "text-red-700 ring-red-600/20 bg-red-50",
                                    Open: "text-orange-400 bg-orange-50 ring-orange-400/10",
                                  }[data.status] ||
                                  "text-gray-600 bg-gray-50 ring-gray-500/10"
                                }`}
                              >
                                {data.status}
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      <DisplayDataItem
                        title={"Reservation manager"}
                        item1={
                          data.manager != null
                            ? data.manager.first_name
                            : "Not Set"
                        }
                        item2={
                          data.manager != null ? data.manager.last_name : ""
                        }
                        linkTo={
                          data.manager != null
                            ? `/users/${data.manager.id}`
                            : undefined
                        }
                      />
                      <DisplayDataItem
                        title={"Reservation balance"}
                        item1={
                          parseFloat(balance).toFixed(2) + " " + branchCurrency
                        }
                      />
                      <DisplayDataItem
                        title={"Security deposit"}
                        item1={
                          parseFloat(security_deposit).toFixed(2) +
                          " " +
                          branchCurrency
                        }
                      />
                      <DisplayDataItem
                        title={"Vehicle"}
                        item1={
                          data.vehicle != null
                            ? `${data.vehicle.brand.name} ${data.vehicle.model.name}`
                            : data.vehicle_class.name
                        }
                        linkTo={
                          data.vehicle != null
                            ? `/vehicles/${data.vehicle.id}`
                            : undefined
                        }
                      />
                      <DisplayDataItem
                        title={"Plate number"}
                        item1={
                          data.vehicle != null
                            ? data.vehicle.plate
                            : "Website order"
                        }
                      />

                    </div>
                  </div>
                </div>

                {/* Customer Details Section */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
                      Customer Details
                    </h3>
                    <div className="grid grid-cols-1 gap-2">
                      <DisplayDataItem
                        title={"Customer"}
                        item1={data.contact.first_name}
                        item2={data.contact.last_name}
                        linkTo={
                          data.contact != null
                            ? `/customers/${data.contact.id}`
                            : undefined
                        }
                      />
                      <DisplayDataItem
                        title={"Mobile number"}
                        item1={data.contact.phone_number}
                        isPhone={true}
                      />
                      <DisplayDataItem
                        title={"Email"}
                        item1={data.contact.email}
                        isEmail={true}
                      />
                    </div>
                  </div>
                </div>

                {/* Pickup Section */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
                      Pickup
                    </h3>
                    <div className="grid grid-cols-1 gap-2">
                      <DisplayDataItem
                        title={"Pickup captain"}
                        item1={
                          data.captain != null
                            ? data.captain.first_name +
                              " " +
                              data.captain.last_name
                            : "Not Set"
                        }
                        linkTo={
                          data.captain != null
                            ? `/users/${data.captain.id}`
                            : undefined
                        }
                      />
                      <DisplayDataItem
                        title={"Pickup scheduled on"}
                        item1={pickupDate || data.pickup_date}
                        countryCode={data.branch.code}
                        isDate={true}
                      />
                      <DisplayDataItem
                        title={"Picked up on"}
                        item1={data.picked_up_time}
                        placeholder="Not picked up yet"
                        countryCode={data.branch.code}
                        isDate={true}
                      />
                      {data.pickup_fleet_location != null &&
                      data.is_custom_pickup === false ? (
                        <DisplayDataItem
                          title={"Pickup location"}
                          item1={data.pickup_fleet_location.name}
                          isLocation={true}
                        />
                      ) : (
                        <DisplayDataItem
                          title={"Pickup location"}
                          item1={`${data.custom_pickup_street} , ${data.custom_pickup_city} , ${data.custom_pickup_state}`}
                          isLocation={true}
                        />
                      )}
                      {currentTab.name === "Pickup" &&
                        (pickupNote || data.pickup_note) != null && (
                          <DisplayDataItem
                            title={"Pickup note"}
                            item1={pickupNote || data.pickup_note}
                            badgeBg={
                              "text-yellow-700 ring-yellow-600/20 bg-yellow-50"
                            }
                          />
                        )}
                      {data.pickup_note_2 != null && (
                        <DisplayDataItem
                          title={"Captain's pickup note"}
                          item1={data.pickup_note_2}
                          badgeBg={
                            "text-yellow-700 ring-yellow-600/20 bg-yellow-50"
                          }
                        />
                      )}
                    </div>
                  </div>
                </div>

                {/* Return Section */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
                      Return
                    </h3>
                    <div className="grid grid-cols-1 gap-2">
                      <DisplayDataItem
                        title={"Return captain"}
                        item1={
                          data.return_captain != null
                            ? data.return_captain.first_name +
                              " " +
                              data.return_captain.last_name
                            : "Not Set"
                        }
                        linkTo={
                          data.return_captain != null
                            ? `/users/${data.return_captain.id}`
                            : undefined
                        }
                      />
                      <DisplayDataItem
                        title={"Return scheduled on"}
                        item1={returnDate || data.return_date}
                        countryCode={data.branch.code}
                        isDate={true}
                      />
                      <DisplayDataItem
                        title={"Adjusted return date"}
                        item1={latestTimeAdjustment}
                        placeholder="No Adjustments"
                        countryCode={data.branch.code}
                        isDate={true}
                      />
                      <DisplayDataItem
                        title={"Returned on"}
                        item1={data.returned_time}
                        placeholder="Not returned yet"
                        countryCode={data.branch.code}
                        isDate={true}
                      />
                      {data.return_fleet_location != null &&
                      data.is_custom_return === false ? (
                        <DisplayDataItem
                          title={"Return location"}
                          item1={data.return_fleet_location.name}
                          isLocation={true}
                        />
                      ) : (
                        <DisplayDataItem
                          title={"Return location"}
                          item1={`${data.custom_return_street} , ${data.custom_return_city} , ${data.custom_return_state},`}
                          isLocation={true}
                        />
                      )}
                      {currentTab.name === "Return" &&
                        (returnNote || data.return_note) != null && (
                          <DisplayDataItem
                            title={"Return note"}
                            item1={returnNote || data.return_note}
                            badgeBg={
                              "text-yellow-700 ring-yellow-600/20 bg-yellow-50"
                            }
                          />
                        )}
                      {data.return_note_2 != null && (
                        <DisplayDataItem
                          title={"Captain's return note"}
                          item1={data.return_note_2}
                          badgeBg={
                            "text-yellow-700 ring-yellow-600/20 bg-yellow-50"
                          }
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ReservationSummary;
